import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import QRCode from "react-qr-code"
import ARButton from "@/styles/images/AR-Button.svg"
import Modal from "@/components/core/Modal/Modal"
import {
  addAnalyticsForCloseClick,
  addAnalyticsForViewNowClick,
} from "./analytics/analytics"
import { VANTANA_URL_ANDROID, VNTANA_EMBED_BASE_URL } from "@/constants/index"
import { getEncodedId } from "@/components/ImageGallery/v2/utlis"
import { UAParser } from "ua-parser-js"
import { extractPaths } from "@/services/client/extractPaths.client"

const ProductARButton = ({
  width = "",
  sku = "",
  vntanaList = [],
  productInfo = {},
  staticTexts = {},
}) => {
  const [showQR, setShowQR] = useState(false)
  const [arLinks, setArLinks] = useState({ glb: "", usdz: "" })
  const [embedURL, setEmbedURL] = useState("")
  const [scrollPosition, setScrollPosition] = useState(0)

  useEffect(() => {
    let isMounted = true // prevent state update after unmount

    const run = async () => {
      const pUuid = getEncodedId(vntanaList, sku)

      if (!pUuid) {
        if (isMounted) {
          setEmbedURL("")
          setArLinks({ glb: null, usdz: null })
        }
        return
      }

      const url = VNTANA_EMBED_BASE_URL.replace("{pUuid}", pUuid)

      if (isMounted) {
        setEmbedURL(url)
      }

      try {
        const { glb, usdz } = await extractPaths(url)

        if (isMounted) {
          setArLinks({ glb, usdz })
        }
      } catch (err) {
        console.error("Failed to extract AR paths:", err)

        if (isMounted) {
          setArLinks({ glb: null, usdz: null })
        }
      }
    }

    run()

    return () => {
      isMounted = false
    }
  }, [sku, vntanaList])

  const launchIOSAR = usdzUrl => {
    const anchor = document.createElement("a")
    anchor.setAttribute("rel", "ar")
    anchor.setAttribute("href", usdzUrl)

    // Required: add an img inside for AR Quick Look
    const img = document.createElement("img")
    img.src = ARButton?.src || "" // fallback image
    anchor.appendChild(img)

    anchor.click()
  }

  const openAndroidAR = glbUrl => {
    const url = VANTANA_URL_ANDROID.replace(
      "{glburl}",
      encodeURIComponent(glbUrl)
    )

    let didHide = false

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        didHide = true
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    window.location.href = url

    setTimeout(() => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)

      // If page never hid → AR likely failed
      if (!didHide) {
        setShowQR(true)
        addAnalyticsForViewNowClick(productInfo)
        setScrollPosition(window.scrollY)
      }
    }, 500)
  }

  const handleClick = async () => {
    const uaString = navigator.userAgent
    const ua = new UAParser(uaString)
    const deviceType = ua.getDevice().type || "desktop"

    const isIOS = /iPhone|iPad|iPod/i.test(uaString)

    if (deviceType === "mobile") {
      if (arLinks.glb || arLinks.usdz) {
        if (isIOS) {
          launchIOSAR(arLinks.usdz)
        } else {
          openAndroidAR(arLinks.glb)
        }
      } else {
        setShowQR(true)
        addAnalyticsForViewNowClick(productInfo)
        setScrollPosition(window.scrollY)
      }
    } else {
      setShowQR(true)
      addAnalyticsForViewNowClick(productInfo)
      setScrollPosition(window.scrollY)
    }
  }

  const closeModal = () => {
    setShowQR(false)
    addAnalyticsForCloseClick(productInfo)
    // restore the scroll position after closing the modal
    if (scrollPosition) {
      window.scrollTo({
        top: Number.parseInt(scrollPosition, 10),
        behavior: "instant",
      })
    }
    setScrollPosition(0)
  }

  return (
    <>
      <div
        className="gallery-image-ar-button"
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label="Open AR Model"
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleClick(e)
          }
        }}
      >
        <span className="ar-button">
          <img src={ARButton?.src} alt="AR" />
        </span>
        <span className="ar-button-label">
          {width < 1023
            ? staticTexts.viewInARMobile
            : staticTexts.viewInARDesktop}
        </span>
      </div>
      {showQR && (
        <div className="product-detail-page__3d-modal">
          <div className="product-detail-page__3d-modal--modal-popup">
            <Modal
              showModal={showQR}
              onModalClose={closeModal}
              isWhiteCloseIcon={true}
            >
              <div className="ar-scanner-container">
                <div className="ar-scanner-body">
                  <div className="ar-scanner-header">{staticTexts.scanQR}</div>
                  <div className="ar-scanner-instruction">
                    {staticTexts.viewInYourSpace}
                  </div>
                  <QRCode
                    size={150}
                    style={{ height: "auto" }}
                    value={embedURL}
                    viewBox={`0 0 150 150`}
                  />
                </div>
              </div>
            </Modal>
          </div>
        </div>
      )}
    </>
  )
}

ProductARButton.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sku: PropTypes.string,
  vntanaList: PropTypes.arrayOf(PropTypes.string),
  productInfo: PropTypes.object,
  staticTexts: PropTypes.shape({
    viewInARMobile: PropTypes.string,
    viewInARDesktop: PropTypes.string,
    scanQR: PropTypes.string,
    viewInYourSpace: PropTypes.string,
  }),
}

export default ProductARButton
